import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Search, Edit2, X, Check, Phone, Mail, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactDirectory = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', work_area: '', role: '', department: '', status: 'active' });

  const fetchContacts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/contacts`);
      setContacts(res.data);
    } catch { toast.error("Failed to load contacts"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const resetForm = () => {
    setForm({ name: '', email: '', phone: '', work_area: '', role: '', department: '', status: 'active' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    try {
      if (editingId) {
        await axios.put(`${API}/contacts/${editingId}`, form);
        toast.success("Contact updated");
      } else {
        await axios.post(`${API}/contacts`, form);
        toast.success("Contact added");
      }
      resetForm();
      fetchContacts();
    } catch { toast.error("Failed to save contact"); }
  };

  const handleEdit = (contact) => {
    setForm(contact);
    setEditingId(contact.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    try {
      await axios.delete(`${API}/contacts/${id}`);
      toast.success("Contact deleted");
      fetchContacts();
    } catch { toast.error("Failed to delete"); }
  };

  const filtered = contacts.filter(c => {
    const q = search.toLowerCase();
    return !q || c.name?.toLowerCase().includes(q) || c.work_area?.toLowerCase().includes(q) || c.role?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q);
  });

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--navy)' }} /></div>;

  return (
    <div className="animate-fadeIn" data-testid="contact-directory-page">
      <div className="breadcrumb">
        <Link to="/">Dashboard</Link><span>&rsaquo;</span><span>Contact Directory</span>
      </div>

      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">Master Contact Directory</h1>
          <p className="page-subtitle">Track responsible persons, contact details, and work areas</p>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-contact-btn"
          style={{ background: 'var(--navy)', color: '#fff' }}>
          <Plus className="w-4 h-4 mr-2" /> Add Contact
        </Button>
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: '20px', maxWidth: '400px' }}>
        <Search className="w-4 h-4" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-light)' }} />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, work area, role..."
          style={{ paddingLeft: '36px' }} data-testid="contact-search" />
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="form-card" style={{ marginBottom: '20px', border: '2px solid var(--navy)' }} data-testid="contact-form">
          <div style={{ padding: '20px' }}>
            <div style={{ fontWeight: 600, color: 'var(--navy)', marginBottom: '16px' }}>
              {editingId ? 'Edit Contact' : 'Add New Contact'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px' }}>
              <div>
                <Label>Name *</Label>
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="Full name" data-testid="contact-name" />
              </div>
              <div>
                <Label>Email</Label>
                <Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="email@example.com" data-testid="contact-email" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                  placeholder="+91-XXXXXXXXXX" data-testid="contact-phone" />
              </div>
              <div>
                <Label>Work Area</Label>
                <Input value={form.work_area} onChange={e => setForm(p => ({ ...p, work_area: e.target.value }))}
                  placeholder="e.g. HVAC, Housekeeping, Electrical" data-testid="contact-work-area" />
              </div>
              <div>
                <Label>Role / Designation</Label>
                <Input value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}
                  placeholder="e.g. Engineer In-Charge, Supervisor" data-testid="contact-role" />
              </div>
              <div>
                <Label>Department</Label>
                <Input value={form.department} onChange={e => setForm(p => ({ ...p, department: e.target.value }))}
                  placeholder="e.g. Engineering, Admin, Operations" />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <Button variant="outline" onClick={resetForm}><X className="w-4 h-4 mr-1" /> Cancel</Button>
              <Button onClick={handleSave} style={{ background: 'var(--navy)', color: '#fff' }} data-testid="save-contact-btn">
                <Check className="w-4 h-4 mr-1" /> {editingId ? 'Update' : 'Add Contact'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Contacts Table */}
      {filtered.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)', background: 'var(--gray-light)', borderRadius: 'var(--radius)' }}>
          {contacts.length === 0 ? 'No contacts added yet' : 'No contacts match your search'}
        </div>
      ) : (
        <div style={{ border: '1px solid var(--gray-border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
          <table className="contract-table" style={{ marginBottom: 0 }}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Work Area</th>
                <th>Role</th>
                <th>Department</th>
                <th style={{ width: '80px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(contact => (
                <tr key={contact.id} style={{ cursor: 'default' }}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{
                        width: '30px', height: '30px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'var(--navy)', color: '#fff', fontWeight: 600, fontSize: '11px', flexShrink: 0
                      }}>
                        {contact.name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                      </div>
                      <span style={{ fontWeight: 500 }}>{contact.name}</span>
                    </div>
                  </td>
                  <td>
                    {contact.email && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}>
                        <Mail className="w-3 h-3" style={{ color: 'var(--text-light)' }} />
                        {contact.email}
                      </div>
                    )}
                  </td>
                  <td>
                    {contact.phone && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}>
                        <Phone className="w-3 h-3" style={{ color: 'var(--text-light)' }} />
                        {contact.phone}
                      </div>
                    )}
                  </td>
                  <td><span style={{ fontSize: '13px' }}>{contact.work_area || '-'}</span></td>
                  <td><span style={{ fontSize: '13px' }}>{contact.role || '-'}</span></td>
                  <td><span style={{ fontSize: '13px' }}>{contact.department || '-'}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button className="icon-btn" onClick={() => handleEdit(contact)} title="Edit">
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button className="icon-btn" onClick={() => handleDelete(contact.id)} title="Delete" style={{ color: 'var(--red)' }}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: '12px', fontSize: '12px', color: 'var(--text-muted)' }}>
        {filtered.length} of {contacts.length} contacts
      </div>
    </div>
  );
};

export default ContactDirectory;
